import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice, SuccessNotice } from '../MissionShell/MissionShell';
import { RevenueSplitSummary, splitRevenue } from '../../api/batch4Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload the transaction workbook (.xlsx). The sheet "Research Services Transactions" is used (or the active sheet).',
  'Required columns: Date, From, To, Credit (GBP).',
  'Click Split Revenue. A ZIP with the Accrud and Deford workbooks downloads automatically.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function RevenueSplitterPanel({ mission, onBack }: PanelProps) {
  const [files, setFiles] = useState<File[]>([]);
  const { status, error, result, fail, run } = useFileRun<FileResult<RevenueSplitSummary>>();

  const handleSubmit = async () => {
    if (files.length === 0) {
      fail('Upload the input workbook first.');
      return;
    }
    await run(() => splitRevenue(files[0]), 'Splitting failed.');
  };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS}>
      <FileDrop
        label="Input workbook"
        required
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={files}
        onFiles={setFiles}
      />
      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Splitting…' : 'Split Revenue'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Splitting…'} />}
      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <SuccessNotice>
          <p>✅ {result.filename} downloaded.</p>
          {result.summary && (
            <>
              <p className="mt-1 text-xs text-green-800">{result.summary.summary}</p>
              <ul className="mt-1 list-disc pl-5 text-xs text-green-800">
                {result.summary.files.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </>
          )}
        </SuccessNotice>
      )}
      {status === 'idle' && <InfoNotice>📁 Upload an Excel file above to generate the Accrud and Deford outputs.</InfoNotice>}
    </MissionShell>
  );
}

export default RevenueSplitterPanel;
